import LegalTabBar from '../../components/LegalTabBar'
import { LegalContainer, LegalH1, LegalHeader, LegalLink, LegalList } from '../../components/OtherLegalComponents'
import {
	Bold, StyledLink, Table, TR, TH, TD
} from '../../../Global/GobalStyledComponents'
import { useEffect } from 'react'
import FaqsHelpHeader from '../FaqsHelpHeader'
import { handleGA4PageHit } from '../../../Global/GA4Functions'

const CPRA = () => {
	useEffect(() => {
		document.title = "CPRA | TitanTV";
		document.querySelector('meta[name="description"]')?.setAttribute('content', "Contains information on the California Consumer Protection Act.");
		document.querySelector('meta[name="robots"]')?.setAttribute('content', "index");
		window.scrollTo(0, 0);
		handleGA4PageHit({ page: '/cpra', title: 'CPRA' });
	})

	return (
		<>
			<FaqsHelpHeader />
			<LegalTabBar />
			<LegalContainer>
				<LegalH1>California Privacy Notice</LegalH1>
				<br />
				<LegalHeader>Effective as of January 1, 2023 (CPRA)</LegalHeader>
				<LegalHeader>Updated May 7, 2026</LegalHeader>
				<p>
					At <Bold>TitanTV, Inc.</Bold> (“TITANTV”, “<Bold>we</Bold>”, “<Bold>us</Bold>” or “<Bold>our</Bold>”, herein), we recognize the importance of privacy to users of our websites at www.TitanTV.com
					(“<Bold>TitanTV.com</Bold>”) and www.AntennaWeb.org (“<Bold>AntennaWeb.org</Bold>”), and of the TitanTV.com executable code version of the software provided by TITANTV (including associated documentation
					and services) (the “<Bold>TitanTV.com Mobile Application</Bold>”, and with the services provided through the websites, collectively, the “Services”). This CPRA privacy policy
					(“CPRA Privacy Policy”) explains what type of information may be collected from you or that you may provide when you access the Services and how that information may be used.
					Please note that this CPRA Privacy Policy pertains only to the Services, and is separate and distinct from the privacy policies, end user license agreements, terms or service, or terms of use governing:
					(a) the web sites, applications and services of TITANTV's clients; (b) the web sites, applications and services that may link to or from the Services; and (c) other TITANTV web sites, applications or services.
				</p>
				<p>
					Please read this CPRA Privacy Policy carefully to understand our policies and practices regarding the information collected from you or that you may provide when you access the Services
					and how we will treat it. If you do not agree with our policies and practices, your choice is not to use the Services. By downloading, accessing or using the Services,
					you agree to this CPRA Privacy Policy. This CPRA Privacy Policy may change from time to time (see Changes to Our CPRA Privacy Policy). Your continued use of the Services after we make
					changes is deemed to be acceptance of those changes, so please check the Privacy Policy periodically for updates
				</p>

				<Bold>1. Scope</Bold>
				<p>
					This California Privacy Notice (this “California Notice”) is issued pursuant to the <Bold>California Privacy Rights Act of 2020 (“CPRA”)</Bold>,
					which amends and supersedes the California Consumer Privacy Act of 2018 (“CCPA”). It applies to you if you are a natural resident of the State of California (“California Consumer” or “you”).
				</p>
				<p>
					This California Notice supplements the information contained in our Privacy Policy and describes
					how we collect, use, disclose, and otherwise process personal information of California Consumers,
					either online or offline, within the scope of the CCPA.
				</p>
				<p>
					Unless otherwise expressly stated, all terms in this California Notice have the same meaning
					as defined in our <StyledLink to='/privacy'>Privacy Policy</StyledLink> or as otherwise defined in the CCPA.
				</p>

				<Bold> 2. Personal Information Disclosures</Bold>
				<p>
					When we use the term “<Bold>personal information</Bold>” in this California Notice, we mean information that
					identifies, relates to, describes, is reasonably capable of being associated with, or could
					reasonably be linked, directly or indirectly, with a particular California Consumer or household.
				</p>
				<p>
					For the purposes of this California Notice, personal information does not include:
				</p>
				<LegalList>
					<li>Publicly available information from government records.</li>
					<li>
						Deidentified, aggregated or anonymized information that is maintained in a form that is not
						capable of being associated with or linked to a California Consumer.
					</li>
					<li>Information excluded from the CCPA's scope, such as:</li>
					<LegalList>
						<li>
							Health or medical information covered by HIPAA and the California Confidentiality of Medical Information Act (CMIA) or clinical trial data;
						</li>
						<li>
							Information covered by sector-specific privacy laws, including the Fair Credit Reporting Act (FCRA),
							the Gramm-Leach-Bliley Act (GLBA), California Financial Information Privacy Act (FIPA), and the Driver’s Privacy Protection Act of 1994.
						</li>
					</LegalList>
					<li>nformation relating to our job applicants, employees, contractors and other TitanTV personnel.</li>
				</LegalList>


				<Bold>Sensitive Personal Information</Bold>
				<p>
					The CPRA creates a new category of <Bold>Sensitive Personal Information (“SPI”)</Bold>, which includes: Social Security numbers or government-issued IDs;
					financial account credentials; precise geolocation data; racial or ethnic origin; religious or philosophical beliefs; union membership; contents of mail,
					email, or text messages (unless TitanTV is the intended recipient); genetic data; biometric data processed to uniquely identify a person; health data; and
					data concerning sex life or sexual orientation.
				</p>
				<p>
					We do not intentionally collect most categories of Sensitive Personal Information, such as Social Security numbers, government-issued identification numbers,
					financial account credentials, racial or ethnic origin, religious beliefs, biometric identifiers, health information, or genetic data.
					Depending on how you use the Services, we may collect location-related information, such as ZIP code, address, IP address, or device/browser location signals,
					to provide antenna signal predictions, local television listings, advertising, analytics, security, and service functionality.
				</p>

				<Bold>4. Collection and Use of Personal Information</Bold>
				<p>
					We collect personal information from and about California Consumers for a variety of purposes. To learn more, please refer to
					Sections 1–5 of our CPRA Privacy Policy (Information We Collect; Information You Provide; Information Collected Automatically; Use of Information; Disclosure of Information).
				</p>
				<p>
					We have collected the following categories of personal information from the sources described below:
				</p>
				<Table>
					<thead>
						<TR>
							<TH>Category of Personal Information Collected </TH>
							<TH>Collected</TH>
							<TH>Sources</TH>
						</TR>
					</thead>
					<tbody>
						<TR>
							<TD>
								Identifiers, such as your name, address, IP address, email address, or other similar identifiers
							</TD>
							<TD>Yes</TD>
							<TD>Directly from you</TD>
						</TR>
						<TR>
							<TD>
								We do not intentionally collect most categories of Sensitive Personal Information, such as Social Security numbers,
								government-issued identification numbers, financial account credentials, racial or ethnic origin, religious beliefs,
								biometric identifiers, health information, or genetic data. Depending on how you use the Services, we may collect
								location-related information, such as ZIP code, address, IP address, or device/browser location signals, to provide
								antenna signal predictions, local television listings, advertising, analytics, security, and service functionality.
							</TD>
							<TD>No</TD>
							<TD>Directly from you
								Your browser or device
							</TD>
						</TR>
						<TR>
							<TD>
								Characteristics of protected classes or groups under state or federal law, such as race, sex, or marital status
							</TD>
							<TD>
								No
							</TD>
							<TD>
								N/A
							</TD>
						</TR>
						<TR>
							<TD>
								Commercial Information, such as information regarding your use of our services
							</TD>
							<TD>Yes</TD>
							<TD>Directly from you
								Your browser or device or from your use of the TitanTV Mobile Application

							</TD>
						</TR>
						<TR>
							<TD>Biometric information used solely for non-identification purposes (e.g., audio recordings)</TD>
							<TD>No</TD>
							<TD>N/A</TD>
						</TR>
						<TR>
							<TD>
								Internet/Network Information, such as browsing history, search history, client ID, and interaction with our websites or advertisements
							</TD>
							<TD>Yes</TD>
							<TD>
								Your browser or device or from your use of the TitanTV.com Mobile Application
							</TD>
						</TR>
						<TR>
							<TD>
								Location-related information," such as zip codes, market area, user-entered address, IP-derived location, or device/browser location signals
							</TD>
							<TD>Yes</TD>
							<TD>
								<p>
									Directly from you
								</p>
								Your browser or device or from your use of the TitanTV.com Mobile Application
							</TD>
						</TR>
						<TR>
							<TD>
								Sensory Information, such as audio or electronic information
							</TD>
							<TD>No</TD>
							<TD>N/A</TD>
						</TR>
						<TR>
							<TD>Professional or employment-related information</TD>
							<TD>No</TD>
							<TD>N/A</TD>
						</TR>
						<TR>
							<TD>
								Inferences, including information generated from your use of the websites reflecting predictions about your preferences
							</TD>
							<TD>Yes</TD>
							<TD>Your browser or device or from your use of the TitanTV.com Mobile Application </TD>
						</TR>
					</tbody>
				</Table>

				<Bold>Purposes for Collecting Personal Information</Bold>
				<p>
					We collect, use, or disclose your personal information for one or more business or commercial purposes, including:
				</p>
				<LegalList>
					<li>to present, manage, or administer the Services and its content to you;</li>
					<li>to optimize or customize the delivery of advertising or content to you;</li>
					<li>to contact you and respond to your messages;</li>
					<li>to carry out our obligations and enforce our rights arising from any contracts entered into between you and us; and</li>
					<li>for data security and fraud prevention purposes;</li>
					<li>to comply with legal obligations; and</li>
					<li>for other business purposes specified in our Privacy Policy or at the time the information is gathered.</li>
				</LegalList>
				<p>
					<Bold>Data Minimization & Retention:</Bold> Consistent with the CPRA, we collect only the personal information reasonably necessary for the disclosed purposes,
					and we retain it only for as long as necessary to fulfill those purposes or as required by law.
				</p>
				<Bold>5. Disclosure of Personal Information</Bold>
				<p>
					We may disclose your personal information to the following categories of recipients for a business purpose:
				</p>
				<LegalList>
					<li>TitanTV’s clients, suppliers, licensors, advertising partners, service vendors, and contractors (who
						are contractually required to keep your information confidential and use it only for the services they provide to us);
					</li>
					<li>
						a buyer or other successor in the event of a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of TitanTV assets;
					</li>
					<li>
						government or regulatory authorities, if required by law or legal process;
					</li>
					<li>
						parties to protect against misuse, unauthorized use of the Services, or to protect personal safety or property;
					</li>
					<li>
						for any other purpose disclosed by us when you provide the information; and
					</li>
					<li>
						with your consent.
					</li>
				</LegalList>

				<Bold>6. Sale or Sharing of Personal Information</Bold>
				<p>
					Advertising is a core part of TitanTV’s business. Our free Services are supported by the ads we and others display on the Services,
					including on TitanTV.com, AntennaWeb.org, and the TitanTV Mobile Application.
				</p>
				<p>
					TitanTV enables its third-party ad partners to place cookies, web beacons, and other similar technologies (“Marketing Cookies”) on your device when you use the Services.
					Third-party ad partners use these Marketing Cookies to collect information about your use of our Services in order to serve targeted advertisements. Making personal
					information available to our third-party ad partners may constitute a <Bold>“sale” or “sharing”</Bold> under the CPRA (including disclosures for cross-context behavioral advertising, even without monetary consideration).
				</p>
				<p>
					If you wish to opt out of the sale or sharing of your personal information, please reach out to privacy@titantv.com.
				</p>
				<Bold>7. California Consumers’ Privacy Rights</Bold>
				<p>
					As a California resident, you may exercise the following rights in relation to the personal information we have collected about you (subject to certain limitations at law):
				</p>
				<Table>
					<thead>
						<TH>Right</TH>
						<TH>Description</TH>
					</thead>
					<tbody>
						<TR>
							<TD><Bold>The Right to Know</Bold></TD>
							<TD>
								<p>
									You have the right to request the following information relating to your personal information we have collected and disclosed:
								</p>
								<LegalList>
									<li>The specific pieces of personal information we have collected about you;</li>
									<li>The categories of personal information collected;</li>
									<li>The categories of sources of the personal information;</li>
									<li>
										The categories of personal information disclosed to third parties for a business purpose, and the categories of recipients;
									</li>
									<li>
										The categories of personal information sold or shared about you (if any), and the categories of third parties; and
									</li>
									<li>
										The business or commercial purposes for collecting, selling, or sharing the personal information.
										Note: Unlike under the CCPA, the CPRA removes the prior 12-month lookback limitation.
									</li>
								</LegalList>
							</TD>
						</TR>
						<TR>
							<TD>
								The Right to Correct
							</TD>
							<TD>
								You have the right to request that we correct inaccurate personal information we maintain about you, taking into account the nature of the information and the purposes of processing it.
							</TD>
						</TR>
						<TR>
							<TD><Bold>The Right to Delete</Bold></TD>
							<TD>
								You have the right to request the deletion of personal information we have collected from you, subject to certain exceptions.
							</TD>
						</TR>
						<TR>
							<TD>The Right to Opt Out of Sale or Sharing</TD>
							<TD>
								You have the right to direct us not to sell or share your personal information to third parties now or in the future. "Sharing" includes disclosing personal information for cross-context behavioral advertising purposes, even without monetary consideration.
								If you are under 16, you have the right to opt in, or to have a parent or guardian opt in on your behalf, to such sales or sharing.
							</TD>
						</TR>
						<TR>
							<TD>
								The Right to Limit Use of Sensitive Personal Information
							</TD>
							<TD>
								You have the right to direct us to limit the use and disclosure of your sensitive personal information to purposes permitted by the CPRA (e.g., providing services you requested). This right applies where we use or disclose SPI for purposes beyond what is necessary to perform the services.
							</TD>
						</TR>
						<TR>
							<TD><Bold>The Right to Non-Discrimination</Bold></TD>
							<TD>
								You have the right not to receive discriminatory treatment for exercising any of these rights.
								However, if the exercise of these rights limits our ability to process personal information (such as in the case of a deletion request), we may no longer be able to provide you our services or engage with you in the same manner.
							</TD>
						</TR>
						<TR>
							<TD>“Shine the Light”</TD>
							<TD>
								California residents with an established business relationship with us have rights to know how their information is disclosed to third parties for direct marketing purposes under California’s “Shine the Light” law (Civ. Code §1798.83).
							</TD>
						</TR>
					</tbody>
				</Table>

				<Bold>8. How to Exercise Your Rights</Bold>
				<Bold>Right to Know, Right to Correct, and Right to Delete</Bold>
				<p>
					To submit a request, please contact us by:
				</p>
				<LegalList>
					<li>
						Emailing<LegalLink href='mailto:privacy@TitanTV.com'>privacy@TitanTV.com</LegalLink> with the subject line “California Rights Request”, or
					</li>
					<li>Calling 319-365-5597.</li>
				</LegalList>
				<p>
					We will need to verify your identity before processing your request. We will generally require matching sufficient information you provide us to the information we maintain in our systems. In certain circumstances,
					we may decline a request where we are unable to verify your identity or where an exception applies.
				</p>
				<p>
					Authorized Agents: You may designate an authorized agent to submit a request on your behalf. We may require the agent to provide proof of authorization and may require you to verify your identity directly with us.
				</p>

				<Bold>Right to Opt Out of Sale or Sharing.</Bold>
				<p>
					To opt out of the sale or sharing of your personal information, please reach out to <LegalLink href='mailto:privacy@TitanTV.com'>privacy@TitanTV.com</LegalLink>
				</p>
				<p>
					Additionally, you may use the following industry opt-out tools:
				</p>
				<LegalList>
					<li>
						<Bold>National Advertising Initiative’s (NAI):</Bold>General Opt-Out:
						<LegalLink href='https://optout.networkadvertising.org/'>https://optout.networkadvertising.org/</LegalLink>
						<br />
						<Bold>Mobile Device Choices:</Bold>
						<LegalLink href='https://www.networkadvertising.org/mobile-choice'>https://www.networkadvertising.org/mobile-choice</LegalLink>
					</li>
					<li>
						<Bold>Digital Advertising Alliance (DAA):</Bold> <LegalLink href='https://optout.privacyrights.info/'>https://optout.privacyrights.info/</LegalLink>
					</li>
					<li>
						<Bold>Google Opt-Out:</Bold> Browser add-on for Google Analytics and Google Ads Settings:
						<LegalLink href='https://adssettings.google.com'>https://adssettings.google.com</LegalLink>
					</li>
					<li>
						<Bold>Mobile Ad Settings:</Bold> Refer to your device’s operating system settings (“Limit Ad Tracking” on iOS; “Opt out of Ads Personalization” on Android).
					</li>
				</LegalList>
				<p>
					Please note that opt-outs described above are device- and browser-based. You must opt out on each device and browser where you want your choice to apply.
					Because opt-outs may be stored via cookies, if you clear cookies, you must opt out again. TitanTV does not maintain or control opt-out tools operated by third parties.
				</p>


				<p>
					<Bold>Right to Limit Use of Sensitive Personal Information</Bold>
					<br />
					To request that we limit the use or disclosure of your sensitive personal information, please contact us at <LegalLink href='mailto:privacy@TitanTV.com'>privacy@TitanTV.com.</LegalLink> with the subject line “CPRA SPI Limit Request.”
				</p>
				<Bold>9. Minors Under Age 16</Bold>
				<p>
					Our Services are not directed to children under 13, and we do not knowingly collect personal information from children under 13. We do not knowingly sell or share personal
					information of consumers under 16 years of age.
				</p>

				<Bold>10. California’s “Shine the Light” Law</Bold>
				<p>
					California’s “Shine the Light” law (Civil Code Section §1798.83) permits California residents with an established business relationship with us to request certain information
					regarding our disclosure of certain types of personal information to third parties for their direct marketing purposes during the immediately preceding calendar year. To make such a
					request, please send an email to <LegalLink href='mailto:privacy@TitanTV.com'>privacy@TitanTV.com.</LegalLink>. This request may be made no more than once per calendar year.
				</p>
				<Bold>11. Updates to This California Notice</Bold>
				<p>
					We will update this California Notice from time to time. When we make changes, we will change the “Last Updated” date at the beginning of this Notice. All changes shall be effective from the date
					of publication unless otherwise provided in the notification.
				</p>
				<Bold>12. Contact Us</Bold>
				<p>
					If you have any questions or comments concerning this California Privacy Notice, please contact us at <LegalLink href='mailto:privacy@TitanTV.com'>privacy@TitanTV.com.</LegalLink>
				</p>

			</LegalContainer>
		</>
	)
}

export default CPRA